/**
 * Shared single-flight latch for session-expiry handling.
 *
 * Three independent channels can detect an expired session:
 *   1. `tokenExpirationService` - proactive JWT `exp` timer.
 *   2. `ApiClient` (services/apiClient.ts) - 401 response interceptor.
 *   3. `router/guards` - sees `!currentUser` on a protected navigation.
 *
 * Without coordination each one emits its own "La sesión ha expirada"
 * toast, so one expiry event produces two or three popups. This module
 * lets all three share a single latch: the first channel to detect the
 * event owns the redirect + user-facing toast; the others skip their
 * own toast.
 *
 * Usage:
 *   if (beginSessionExpiry()) {
 *     // we own the flow - show toast, redirect, etc.
 *     endSessionExpiry()
 *   }
 *   // else: someone else is already handling it - stay silent.
 *
 * Safety: the latch auto-releases after `AUTO_RELEASE_MS` in case a
 * caller forgets to call `endSessionExpiry()`. Without this a stuck
 * flag would silently suppress every future expiry toast.
 */

const AUTO_RELEASE_MS = 10_000

let inFlight = false
let autoReleaseTimer: number | null = null

function clearAutoRelease() {
  if (autoReleaseTimer !== null) {
    window.clearTimeout(autoReleaseTimer)
    autoReleaseTimer = null
  }
}

/**
 * Attempt to acquire the session-expiry latch. Returns `true` if the
 * caller now owns the flow; `false` if another channel already does.
 */
export function beginSessionExpiry(): boolean {
  if (inFlight) {
    return false
  }
  inFlight = true
  clearAutoRelease()
  autoReleaseTimer = window.setTimeout(() => {
    inFlight = false
    autoReleaseTimer = null
  }, AUTO_RELEASE_MS)
  return true
}

/**
 * Release the latch. Idempotent.
 */
export function endSessionExpiry(): void {
  inFlight = false
  clearAutoRelease()
}

/**
 * Peek without acquiring. Use this when you want to suppress your own
 * toast but don't intend to take ownership (e.g. the route guard,
 * which still has to redirect the user regardless).
 */
export function isSessionExpiryInFlight(): boolean {
  return inFlight
}
